import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { fmtDT, inr } from "@/lib/brand";
import { RequireAuth } from "@/components/site/Shared";

function ResultInner() {
  const [params] = useSearchParams();
  const id = params.get("booking_id");
  const changeId = params.get("change_id");
  const api = useApi();
  const [b, setB] = useState(null);
  const [ch, setCh] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let tries = 0;
    let timer;
    const check = async () => {
      try {
        if (changeId) {
          const c = (await api.get(`/bookings/${id}/changes/${changeId}`)).data;
          setCh(c);
          if (c.status === "PENDING_PAYMENT" && tries++ < 4) timer = setTimeout(check, 2500);
        }
        const r = await api.get(`/bookings/${id}`);
        setB(r.data);
        if (!changeId && r.data.status === "PENDING_PAYMENT" && tries++ < 4) timer = setTimeout(check, 2500);
      } catch (e) {
        setError(errMsg(e));
      }
    };
    check();
    return () => clearTimeout(timer);
  }, [id, changeId, api]);

  if (error) return <div className="container-x py-24 text-center text-rose-700" data-testid="payment-result-error">{error}</div>;
  if (!b || (changeId && !ch)) return <div className="container-x py-24 text-center text-neutral-500">Checking your payment…</div>;
  const ok = changeId ? ch.status === "APPLIED" : ["CONFIRMED", "ACTIVE", "COMPLETED"].includes(b.status);
  const pending = changeId ? ch.status === "PENDING_PAYMENT" : b.status === "PENDING_PAYMENT";
  const Icon = ok ? CheckCircle2 : pending ? Clock : XCircle;
  const word = ch?.kind === "extension" ? "Trip extended" : "Booking rescheduled";
  const title = changeId ? (ok ? `${word}!` : pending ? "Payment pending" : ch.status === "PAID_NOT_APPLIED" ? "Payment received — change not possible" : "Payment not completed")
    : ok ? "Booking confirmed!" : pending ? "Payment pending" : "Payment not completed";
  const text = changeId
    ? ok ? `New dates are saved for booking ${b.id}.` : pending ? "We haven't received your payment yet. If money was debited, it will update in a few minutes."
      : ch.status === "PAID_NOT_APPLIED" ? `The car was booked by someone else meanwhile. Your ${inr(ch.amount)} will be refunded.` : "No change was made to your booking."
    : ok ? `Your ${b.car_name} is reserved. Booking ID ${b.id}.` : pending ? "We haven't received your payment yet. If money was debited, it will update in a few minutes." : "This booking was not paid. No amount has been charged.";

  return (
    <div className="container-x max-w-2xl py-20" data-testid="payment-result-page">
      <div className="rounded-3xl border border-black/5 bg-white p-10 text-center shadow-sm">
        <Icon className={`mx-auto h-16 w-16 ${ok ? "text-emerald-500" : pending ? "text-amber-500" : "text-rose-500"}`} />
        <h1 className="mt-6 text-3xl font-bold tracking-tight" data-testid="payment-result-title">{title}</h1>
        <p className="mt-3 text-neutral-600">{text}</p>
        <div className="mx-auto mt-8 grid max-w-md grid-cols-2 gap-3 text-left text-sm">
          <div className="rounded-xl bg-neutral-50 p-3"><p className="text-xs text-neutral-500">Pickup</p><p className="font-medium">{fmtDT(b.pickup_at)}</p></div>
          <div className="rounded-xl bg-neutral-50 p-3"><p className="text-xs text-neutral-500">Return</p><p className="font-medium">{fmtDT(b.return_at)}</p></div>
          <div className="col-span-2 rounded-xl bg-neutral-50 p-3"><p className="text-xs text-neutral-500">Amount</p><p className="font-medium">{inr(b.quote.total)}</p></div>
        </div>
        <Link to="/bookings" data-testid="payment-result-bookings-btn" className="mt-8 inline-block rounded-full bg-neutral-950 px-7 py-3 font-semibold text-white transition-colors duration-200 hover:bg-[#FF5A00]">Go to my bookings</Link>
      </div>
    </div>
  );
}

export default function PaymentResult() {
  return <RequireAuth><ResultInner /></RequireAuth>;
}
